import { rotationForDefaultAssetType } from '@ikegps/core-api';
import type { CollectionSummaryAssetType, TagJSONObject, TagSummaryJSONObject } from '@ikegps/core-api';
import { ColorHash } from '@ikegps/core-shared';
import { AssetTypeSwatch } from '../../components/asset.type.swatch';
import { getShowRedDot } from '../../components/red.dot/is.missing.true.size';
import { addStyles } from '../../components/theme';
import { m } from '../../mithril';
import { AppStore } from '../../store';
import { CIRCLE_RADIUS } from '../collection.list/collection.card.style';
import { CSS, Style } from './tag.icon.style';

addStyles(Style);

// `c-TagIcon` itself has no fixed size — it inherits width/height from whichever caller wraps it — so
// this is sized for the one context that actually passes `assetType`: the collection-list dot (see
// `CIRCLE_RADIUS` in `collection.card.style.ts`).
const ASSET_TYPE_ICON_SIZE = CIRCLE_RADIUS * 2;

export const TagIcon = {
	view(
		tag: TagJSONObject,
		summary?: TagSummaryJSONObject,
		color?: string,
		showRedDot?: boolean,
		assetType?: CollectionSummaryAssetType | null,
		/**
		 * Selecting a row shouldn't depend on precisely hitting the (CSS-hover-revealed) checkbox
		 * underneath — this fires the same toggle immediately, on a plain click of the icon/dot itself.
		 * Only `TagCardCheckbox` (the one caller with an actual selection concept) passes this; the
		 * variant-row and job-assign-modal callers leave it undefined and get no click behavior here.
		 */
		onClick?: (e: MouseEvent) => void
	) {
		const value = summary != null ? summary.revisions.length : null;
		showRedDot = showRedDot || TagIcon.checkMissingTrueSize(summary);
		const redDot = showRedDot ? m('span', { className: CSS.redDot }) : null;

		// A collection with a selected (non-default) asset type shows just its bare icon, not a dot — the
		// icon's own color already distinguishes it, so the tag/map-marker colored dot underneath it
		// would be redundant chrome. A collection with no asset type keeps the normal colored dot.
		if (assetType != null) {
			// redDot listed first so it paints behind the icon (same DOM-order convention as the map
			// marker, @see CollectionMapPoint.view in point.collection.ts) — same corner overlap, just
			// underneath the glyph rather than covering it.
			return m('div', { className: CSS.bareContainer, onclick: onClick }, [
				redDot,
				AssetTypeSwatch.view({
					icon: assetType.icon,
					color: assetType.color,
					size: ASSET_TYPE_ICON_SIZE,
					rotate: rotationForDefaultAssetType(assetType.title),
				}),
			]);
		}

		return m(
			'div',
			{
				className: CSS.container,
				style: {
					'border-color': ColorHash.hash(tag.objectId),
					'background-color': color ? color : '#FFFFFF',
				},
				onclick: onClick,
			},
			[redDot, value]
		);
	},

	checkMissingTrueSize(summary?: TagSummaryJSONObject): boolean {
		if (summary == null) {
			return false;
		}

		// If the server-side flag is already set, use it directly
		if (summary.hasCollectionsMissingTrueSize != null) {
			return summary.hasCollectionsMissingTrueSize;
		}

		// Fall back to checking individual collection summaries.
		// getShowRedDot caches computed values on each collection summary.
		for (const revision of summary.revisions) {
			const collSummary = AppStore.CollectionSummary.get(revision);
			if (collSummary != null && getShowRedDot(collSummary)) {
				summary.hasCollectionsMissingTrueSize = true;
				return true;
			}
		}

		return false;
	},
};
